import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { WorkData } from '../dto/EntityRepository';
import { useWorkViewStore } from '../store/WorkViewStore';
import { useAuth } from './useApi/useAuth';
import { useWorkListActions } from './useApi/useWorkList';
import { useEditMode } from './useEditMode';
import { useUnsavedChanges } from './useUnsavedChanges';
import { useWorkListMarqueeStore } from '../store/workListMarqueeStore';
import { useWorkIndexStore } from '../store/WorkIndexStore';
import { useWorkIndexing } from './useWorkIndexing';
import { isContiguousBlock, isCtrlOrMetaClick, isTypingKeyTarget, nextWorkIndexForArrow } from './workListSelectionUtils';
import { useModalStore } from '../store/portal/modalStore';

/** Works 리스트: 편집 모드 다중 선택(Shift/Ctrl·Cmd 클릭, Shift+방향키), 일반 모드 방향키 이동 */
export function useWorkListSelection(
  workList: WorkData[],
  options?: { gridCols?: number },
) {
  const gridCols = options?.gridCols ?? 1;
  const { isOwner } = useAuth();
  const { isEditMode } = useEditMode();
  const { getWork, getWorkOwner, getSelectedWorkListForOwner } = useWorkListActions();
  const { runAfterUnsavedChangesCheck } = useUnsavedChanges();
  const { isIndexingMode } = useWorkIndexing();
  const setActiveWork = useWorkViewStore((s) => s.setActiveWork);
  const [multiSelectedWorkIds, setMultiSelectedWorkIds] = useState<Set<string>>(() => new Set());
  const [selectionAnchorId, setSelectionAnchorId] = useState('');
  const [shiftKeyboardRangeEndId, setShiftKeyboardRangeEndId] = useState<string | null>(null);
  const prevMultiSelectedIdsRef = useRef<Set<string>>(new Set());
  const prevIsEditModeRef = useRef(isEditMode);

  const multiSelectedWorkIdsKey = useMemo(
    () => [...multiSelectedWorkIds].sort().join('|'),
    [multiSelectedWorkIds]
  );

  /**
   * multiSelectedWorkIds(의 key) 변경 후에만 호출. 본문의 multiSelectedWorkIds는 key와 같은 렌더 스냅샷.
   * 편집 모드가 아니면 owner 조회를 호출하지 않음(편집 종료 직후 delta로 API가 다시 나가 multi가 복구되는 것 방지).
   */
  useEffect(() => {
    if (multiSelectedWorkIds.size === 0) {
      const solo = useWorkViewStore.getState().activeWork?.id ?? '';
      prevMultiSelectedIdsRef.current = solo ? new Set([solo]) : new Set();
      return;
    }
    if (!isEditMode) return;
    const prev = prevMultiSelectedIdsRef.current;
    const enteredMultiFromSingle = prev.size <= 1 && multiSelectedWorkIds.size > 1;
    const delta = enteredMultiFromSingle
      ? [...multiSelectedWorkIds]
      : [...multiSelectedWorkIds].filter((id) => !prev.has(id));
    prevMultiSelectedIdsRef.current = new Set(multiSelectedWorkIds);
    if (!isOwner || delta.length === 0) return;
    void getSelectedWorkListForOwner(delta);
  }, [multiSelectedWorkIdsKey, isEditMode, isOwner, getSelectedWorkListForOwner]);

  /** 멀티가 비었을 때 ref + 로컬 id 동기화(API 등 다른 경로의 setActiveWork 대응) */
  useEffect(() => {
    const syncWhenStoreMultiEmpty = () => {
      const state = useWorkViewStore.getState();
      if (state.multiSelectedWork.length > 0) return;
      const solo = state.activeWork?.id ?? '';
      prevMultiSelectedIdsRef.current = solo ? new Set([solo]) : new Set();
      setMultiSelectedWorkIds((prev) => (prev.size === 0 ? prev : new Set()));
    };
    syncWhenStoreMultiEmpty();
    return useWorkViewStore.subscribe(syncWhenStoreMultiEmpty);
  }, []);

  useEffect(() => {
    if (selectionAnchorId && !workList.some((w) => w.id === selectionAnchorId)) {
      setSelectionAnchorId('');
    }
  }, [workList, selectionAnchorId]);

  useEffect(() => {
    if (shiftKeyboardRangeEndId && !workList.some((w) => w.id === shiftKeyboardRangeEndId)) {
      setShiftKeyboardRangeEndId(null);
    }
  }, [workList, shiftKeyboardRangeEndId]);

  /**
   * 편집 모드 종료 시 다중 선택을 해제하고 첫 항목을 단일 active로 맞춤.
   * useLayoutEffect: paint 전에 스토어/로컬 id를 맞춰 Multi 뷰가 한 프레임 보이거나, 이후 passive effect가 API를 다시 쏘는 것을 줄임.
   */
  useLayoutEffect(() => {
    const wasEditMode = prevIsEditModeRef.current;
    prevIsEditModeRef.current = isEditMode;
    if (!wasEditMode || isEditMode) return;

    const { multiSelectedWork } = useWorkViewStore.getState();
    if (multiSelectedWork.length === 0) return;

    const first = multiSelectedWork[0];
    const fromList = workList.find((w) => w.id === first.id) ?? first;

    setMultiSelectedWorkIds(new Set());
    setActiveWork(fromList);
    setSelectionAnchorId(first.id);
    setShiftKeyboardRangeEndId(null);
    void (isOwner ? getWorkOwner(first.id) : getWork(first.id));
  }, [isEditMode, workList, isOwner, getWork, getWorkOwner, setActiveWork]);

  const changeActiveWork = (newId: string) => {
    const { setActiveWork, setMultiSelectedWork } = useWorkViewStore.getState();
    const getWorkWithDetailWithApi = async () => {
      const fromList = workList.find((w) => w.id === newId);
      setMultiSelectedWorkIds(new Set());
      if (fromList) {
        setActiveWork(fromList);
      } else {
        setMultiSelectedWork([]);
      }
      setSelectionAnchorId(newId);
      setShiftKeyboardRangeEndId(null);
      if (isOwner) {
        await getWorkOwner(newId);
      } else {
        await getWork(newId);
      }
    };
    const currentActiveId = useWorkViewStore.getState().activeWork?.id;
    if (newId !== currentActiveId) {
      runAfterUnsavedChangesCheck(() => {
        void getWorkWithDetailWithApi();
      });
    }
  };

  const changeActiveWorkRef = useRef(changeActiveWork);
  changeActiveWorkRef.current = changeActiveWork;
  const workListRef = useRef(workList);
  workListRef.current = workList;

  /** 마퀴 프리뷰(hitWorks)를 WorkView 선택으로 반영 (내부용 — `commitMarqueeHitWorksPreview`만 사용처에서 호출) */
  const finalizeMarqueeHitWorks = useCallback((works: WorkData[]) => {
    if (works.length === 0) return;
    if (!isEditMode) return;

    if (works.length === 1) {
      changeActiveWorkRef.current(String(works[0].id));
      return;
    }

    const store = useWorkViewStore.getState();
    const run = () => {
      setMultiSelectedWorkIds(new Set(works.map((w) => String(w.id))));
      /** multi를 먼저 채운 뒤 active를 비움 — 순서 바뀌면 store subscribe가 ids를 초기화함 */
      store.setMultiSelectedWork(works);
      store.setActiveWork(null);
      setShiftKeyboardRangeEndId(null);
      setSelectionAnchorId(String(works[0].id));
    };
    runAfterUnsavedChangesCheck(run);
  }, [isEditMode, runAfterUnsavedChangesCheck]);

  /**
   * 마퀴 드래그 종료 시 한 번 호출: `hitWorks` 스냅샷 확정 → `WorkView`/로컬 선택 갱신 → 프리뷰 스토어 비움
   */
  const commitMarqueeHitWorksPreview = useCallback(() => {
    const { clearHitWorks } = useWorkListMarqueeStore.getState();
    const snapshot = [...useWorkListMarqueeStore.getState().hitWorks];
    if (isIndexingMode) {
      const { addSelectedIndexingWork } = useWorkIndexStore.getState();
      snapshot.forEach((w) => addSelectedIndexingWork(w));
      clearHitWorks();
      return;
    }
    finalizeMarqueeHitWorks(snapshot);
    clearHitWorks();
  }, [finalizeMarqueeHitWorks, isIndexingMode]);

  const applyShiftRange = (endData: WorkData) => {
    const store = useWorkViewStore.getState();
    const aw = store.activeWork;
    const multiWorks = store.multiSelectedWork;
    const list = workListRef.current;
    const anchor =
      (selectionAnchorId && list.some((w) => w.id === selectionAnchorId) ? selectionAnchorId : '') ||
      aw?.id ||
      endData.id;

    const ai = list.findIndex((w) => w.id === anchor);
    const bi = list.findIndex((w) => w.id === endData.id);
    if (ai === -1 || bi === -1) {
      changeActiveWorkRef.current(endData.id);
      return;
    }
    const lo = Math.min(ai, bi);
    const hi = Math.max(ai, bi);
    const rangeSlice = list.slice(lo, hi + 1);
    const rangeSet = new Set(rangeSlice.map((w) => w.id));
    const multiSet = new Set(multiWorks.map((w) => w.id));
    const mergedWorks =
      multiWorks.length > 0
        ? list.filter((w) => multiSet.has(w.id) || rangeSet.has(w.id))
        : rangeSlice;

    if (mergedWorks.length <= 1) {
      changeActiveWorkRef.current(endData.id);
      return;
    }

    const mergedIds = mergedWorks.map((w) => w.id);
    const selectionUnchanged =
      multiWorks.length > 0 &&
      mergedIds.length === multiWorks.length &&
      mergedIds.every((id, i) => id === multiWorks[i].id);
    if (selectionUnchanged) {
      setShiftKeyboardRangeEndId(endData.id);
      return;
    }

    const run = () => {
      const ids = mergedWorks.map((w) => w.id);
      setMultiSelectedWorkIds(new Set(ids));
      store.setMultiSelectedWork(mergedWorks);
      setShiftKeyboardRangeEndId(endData.id);
    };
    runAfterUnsavedChangesCheck(run);
  };

  const applyShiftRangeRef = useRef(applyShiftRange);
  applyShiftRangeRef.current = applyShiftRange;
  const shiftKeyboardRangeEndIdRef = useRef(shiftKeyboardRangeEndId);
  shiftKeyboardRangeEndIdRef.current = shiftKeyboardRangeEndId;

  const extendGridSelectionByArrow = (key: string) => {
    const list = workListRef.current;
    const store = useWorkViewStore.getState();
    const aw = store.activeWork;
    const multiWorks = store.multiSelectedWork;

    const idSet = new Set<string>();
    if (multiWorks.length > 0) {
      multiWorks.forEach((w) => idSet.add(w.id));
    } else if (aw) {
      idSet.add(aw.id);
    } else {
      return;
    }

    const indices = list.map((w, i) => (idSet.has(w.id) ? i : -1)).filter((i) => i >= 0);
    if (indices.length === 0) return;
    const minIdx = Math.min(...indices);
    const maxIdx = Math.max(...indices);
    const endFromState = shiftKeyboardRangeEndIdRef.current
      ? list.findIndex((w) => w.id === shiftKeyboardRangeEndIdRef.current)
      : -1;
    const fallbackEnd = (key === 'ArrowLeft' || key === 'ArrowUp') ? minIdx : maxIdx;
    const endIndex = endFromState >= 0 ? endFromState : fallbackEnd;
    const nextIndex = nextWorkIndexForArrow(endIndex, key, list.length, gridCols);
    if (nextIndex == null) return;

    const anchorId =
      (selectionAnchorId && list.some((w) => w.id === selectionAnchorId) ? selectionAnchorId : '') ||
      aw?.id ||
      list[endIndex].id;
    const ai = list.findIndex((w) => w.id === anchorId);
    if (ai === -1) return;

    const lo = Math.min(ai, nextIndex);
    const hi = Math.max(ai, nextIndex);
    const rangeSlice = list.slice(lo, hi + 1);
    if (rangeSlice.length <= 1) {
      changeActiveWorkRef.current(rangeSlice[0].id);
      return;
    }

    const mergedIds = rangeSlice.map((w) => w.id);
    const selectionUnchanged =
      multiWorks.length > 0 &&
      mergedIds.length === multiWorks.length &&
      mergedIds.every((id, i) => id === multiWorks[i].id);
    if (selectionUnchanged) {
      setShiftKeyboardRangeEndId(list[nextIndex].id);
      return;
    }

    const run = () => {
      setMultiSelectedWorkIds(new Set(mergedIds));
      store.setMultiSelectedWork(rangeSlice);
      setShiftKeyboardRangeEndId(list[nextIndex].id);
      setSelectionAnchorId(anchorId);
    };
    runAfterUnsavedChangesCheck(run);
  };
  const extendGridSelectionByArrowRef = useRef(extendGridSelectionByArrow);
  extendGridSelectionByArrowRef.current = extendGridSelectionByArrow;

  useEffect(() => {
    if (gridCols <= 1) return;

    const isArrowKey = (key: string) =>
      key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp' || key === 'ArrowDown';

    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (isIndexingMode) return;
      if (useModalStore.getState().standardModal !== null) return;
      if (isTypingKeyTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (!isArrowKey(e.key)) return;

      const list = workListRef.current;
      if (!list.length) return;

      if (isEditMode) {
        if (!e.shiftKey) return;
        e.preventDefault();
        extendGridSelectionByArrowRef.current(e.key);
        return;
      }

      if (e.shiftKey) return;
      const activeWorkId = useWorkViewStore.getState().activeWork?.id ?? '';
      const activeIndex = list.findIndex((each) => each.id === activeWorkId);
      const nextIndex = nextWorkIndexForArrow(activeIndex, e.key, list.length, gridCols);
      if (nextIndex == null) return;
      e.preventDefault();
      changeActiveWorkRef.current(list[nextIndex].id);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [gridCols, isEditMode, isIndexingMode]);

  const extendMultiSelectionByArrow = (direction: 'up' | 'down') => {
    const store = useWorkViewStore.getState();
    const aw = store.activeWork;
    const multiWorks = store.multiSelectedWork;

    const idSet = new Set<string>();
    if (multiWorks.length > 0) {
      multiWorks.forEach((w) => idSet.add(w.id));
    } else if (aw) {
      idSet.add(aw.id);
    } else {
      return;
    }

    const indices = workList.map((w, i) => (idSet.has(w.id) ? i : -1)).filter((i) => i >= 0);
    if (indices.length === 0) return;
    const minIdx = Math.min(...indices);
    const maxIdx = Math.max(...indices);
    const contiguous = isContiguousBlock(minIdx, maxIdx, idSet, workList);

    const applyMulti = (mergedIds: string[], focusId: string) => {
      if (mergedIds.length <= 1) {
        if (mergedIds.length === 1) changeActiveWork(mergedIds[0]);
        return;
      }
      const mergedIdSet = new Set(mergedIds);
      const mergedWorksList = workList.filter((w) => mergedIdSet.has(w.id));
      const run = () => {
        const ids = mergedWorksList.map((w) => w.id);
        setMultiSelectedWorkIds(new Set(ids));
        store.setMultiSelectedWork(mergedWorksList);
        setShiftKeyboardRangeEndId(focusId);
      };
      runAfterUnsavedChangesCheck(run);
    };

    if (direction === 'down') {
      if (maxIdx >= workList.length - 1) return;
      const newId = workList[maxIdx + 1].id;
      const nextSet = new Set(idSet);
      nextSet.add(newId);
      const mergedIds = workList.filter((w) => nextSet.has(w.id)).map((w) => w.id);
      applyMulti(mergedIds, newId);
      return;
    }

    if (contiguous) {
      if (minIdx > 0) {
        const mergedIds = workList.slice(minIdx - 1, maxIdx + 1).map((w) => w.id);
        applyMulti(mergedIds, workList[minIdx - 1].id);
      } else {
        if (maxIdx <= minIdx) return;
        const mergedIds = workList.slice(minIdx, maxIdx).map((w) => w.id);
        if (mergedIds.length === 1) {
          changeActiveWork(mergedIds[0]);
          return;
        }
        applyMulti(mergedIds, workList[maxIdx - 1].id);
      }
      return;
    }

    const prevIdx = maxIdx - 1;
    if (prevIdx >= minIdx && !idSet.has(workList[prevIdx].id)) {
      const mergedIds = workList.slice(minIdx, maxIdx).map((w) => w.id);
      if (mergedIds.length === 1) {
        changeActiveWork(mergedIds[0]);
        return;
      }
      applyMulti(mergedIds, workList[maxIdx - 1].id);
      return;
    }

    const maxId = workList[maxIdx].id;
    idSet.delete(maxId);
    const mergedIds = workList.filter((w) => idSet.has(w.id)).map((w) => w.id);
    if (mergedIds.length === 0) {
      runAfterUnsavedChangesCheck(() => {
        setMultiSelectedWorkIds(new Set());
        store.setMultiSelectedWork([]);
        setSelectionAnchorId('');
        setShiftKeyboardRangeEndId(null);
      });
      return;
    }
    if (mergedIds.length === 1) {
      changeActiveWork(mergedIds[0]);
      return;
    }
    const remaxIdx = Math.max(
      ...mergedIds.map((id) => workList.findIndex((w) => w.id === id)).filter((i) => i >= 0)
    );
    applyMulti(mergedIds, workList[remaxIdx].id);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (isIndexingMode) return;
    if (!workList.length) return;
    // 그리드는 포커스가 컨테이너에 없어도 동작하도록 window 리스너에서 처리. 여기선 중복 이동 방지.
    if (gridCols > 1) return;

    if (isEditMode && e.shiftKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      extendMultiSelectionByArrow(e.key === 'ArrowDown' ? 'down' : 'up');
      return;
    }

    if (isEditMode) return;

    const activeWorkId = useWorkViewStore.getState().activeWork?.id ?? '';
    const activeIndex = workList.findIndex((each) => each.id === activeWorkId);
    const nextIndex = nextWorkIndexForArrow(activeIndex, e.key, workList.length, 1);
    if (nextIndex == null) return;
    e.preventDefault();
    changeActiveWork(workList[nextIndex].id);
  };

  const handleWorkClick = useCallback((data: WorkData, e: MouseEvent) => {
    if (isIndexingMode) return;
    if (!isEditMode) {
      changeActiveWorkRef.current(data.id);
      return;
    }
    if (e.shiftKey) {
      applyShiftRangeRef.current(data);
      return;
    }

    const ctrl = isCtrlOrMetaClick(e);
    const store = useWorkViewStore.getState();
    const { activeWork: aw, multiSelectedWork: multiWorks } = store;
    const multiEmpty = multiWorks.length === 0;
    const inMulti = multiWorks.some((w) => w.id === data.id);

    if (!multiEmpty) {
      if (!inMulti) {
        if (ctrl) {
          runAfterUnsavedChangesCheck(() => {
            const nextIds = [...multiWorks.map((w) => w.id), data.id];
            setMultiSelectedWorkIds(new Set(nextIds));
            store.addMultiSelectedWork([data]);
            store.setMultiHasChanged(false);
            setSelectionAnchorId(data.id);
            setShiftKeyboardRangeEndId(data.id);
          });
          return;
        }
        changeActiveWorkRef.current(data.id);
        return;
      }
      const next = multiWorks.filter((w) => w.id !== data.id);
      if (next.length === 0) {
        runAfterUnsavedChangesCheck(() => {
          setMultiSelectedWorkIds(new Set());
          store.setMultiSelectedWork([]);
          setSelectionAnchorId('');
          setShiftKeyboardRangeEndId(null);
        });
        return;
      }
      if (next.length === 1) {
        const [only] = next;
        changeActiveWorkRef.current(only.id);
        return;
      }
      runAfterUnsavedChangesCheck(() => {
        setMultiSelectedWorkIds(new Set(next.map((w) => w.id)));
        store.setMultiSelectedWork(next);
        setShiftKeyboardRangeEndId(null);
      });
      return;
    }

    if (!aw) {
      changeActiveWorkRef.current(data.id);
      return;
    }

    if (!ctrl) {
      changeActiveWorkRef.current(data.id);
      return;
    }

    if (aw.id === data.id) return;

    const prevActive = aw;
    const prevFromList =
      workListRef.current.find((work) => work.id === prevActive.id) ?? prevActive;
    const enterMulti = () => {
      setMultiSelectedWorkIds(new Set([prevActive.id, data.id]));
      setSelectionAnchorId(prevActive.id);
      setShiftKeyboardRangeEndId(data.id);
      store.setMultiSelectedWork([prevFromList, data]);
    };
    runAfterUnsavedChangesCheck(enterMulti);
  }, [isIndexingMode, isEditMode, runAfterUnsavedChangesCheck]);

  const handleWorkListBackgroundClick = (e: MouseEvent<HTMLElement>) => {
    if (isIndexingMode) return;
    const t = e.target as HTMLElement;
    if (t.closest('[data-work-id]')) return;
    if (t.closest('[data-suppress-marquee]')) return;
    if (!isEditMode) return;
    const multi = useWorkViewStore.getState().multiSelectedWork;
    if (multi.length < 2) return;

    const first = multi[0];
    const newId = String(first.id);
    const fromList = workList.find((w) => w.id === first.id) ?? first;

    const collapseMultiToFirstActive = async () => {
      const { setActiveWork, setMultiSelectedWork } = useWorkViewStore.getState();
      setActiveWork(fromList);
      setMultiSelectedWork([]);
      setMultiSelectedWorkIds(new Set());
      if (isOwner) {
        await getWorkOwner(newId);
      } else {
        await getWork(newId);
      }
    };

    setSelectionAnchorId(newId);
    setShiftKeyboardRangeEndId(null);
    runAfterUnsavedChangesCheck(() => {
      void collapseMultiToFirstActive();
    });
  };

  return { handleWorkClick, handleKeyDown, commitMarqueeHitWorksPreview, handleWorkListBackgroundClick };
}
